import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import primelaneLogo from '@/assets/primelane-logo.png';
import { DesktopNav } from '@/components/navigation/SectionDrawer';
import RequireAuth from '@/components/auth/RequireAuth';

function getActiveSection(pathname: string): string {
    // The agreements list is a top-level path rather than a `group/page` one, so it is
    // matched by name here; the nav entry that lights up for it carries the same id.
    if (pathname.startsWith('/agreements')) return 'agreements';
    if (pathname === '/fleet/procurement') return 'fleet/procurement';
    // A contract's pages (its inspections) sit under the contracts list and light it up.
    if (pathname === '/fleet/contracts' || pathname.startsWith('/fleet/contracts/')) return 'fleet/contracts';
    if (pathname.startsWith('/fleet')) return 'fleet/procurement';
    if (pathname === '/finance/earnings') return 'finance/earnings';
    if (pathname === '/finance/vehicles') return 'finance/earnings';
    if (pathname.startsWith('/finance/payouts')) return 'finance/payouts';
    if (pathname.startsWith('/finance')) return 'finance/earnings';
    return 'overview/summary';
}

/**
 * The chrome. It only ever renders inside `RequireAuth`, so `user` is present and
 * `partnerId` is resolved by the time this component exists — the gate that used to be
 * lines 51-67 of this file now lives in `@/components/auth/RequireAuth`, so that the signing
 * ceremony can be gated the same way WITHOUT inheriting the header and the nav.
 *
 * Nothing about what this renders has changed. `user!` is the narrowing the removed
 * `if (!user || !isAuthorized) return null` used to give the compiler for free; the gate
 * above still guarantees it.
 */
function PartnerChrome() {
    const { user, partnerId, signOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const activeSection = getActiveSection(location.pathname);

    const { data: partner } = useQuery({
        queryKey: ['partner-layout', partnerId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('partners')
                .select('name, created_at')
                .eq('id', partnerId!)
                .single();
            if (error) throw error;
            return data as { name: string; created_at: string };
        },
        enabled: !!partnerId,
    });

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth');
    };

    const handleNavChange = (section: string) => {
        navigate(`/${section}`);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile Header */}
            <header className="lg:hidden sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="flex h-14 items-center px-4">
                    <img src={primelaneLogo} alt="Primelane" className="h-7" />
                </div>
            </header>

            {/* Desktop Header */}
            <header className="hidden lg:block sticky top-0 z-50 border-b border-border bg-background">
                <div className="flex h-16 items-center justify-between px-4 lg:px-6">
                    <img src={primelaneLogo} alt="Primelane" className="h-8" />

                    {/* Center nav - rendered directly, no portal */}
                    <DesktopNav activeSection={activeSection} onSectionChange={handleNavChange} />

                    {/* User & Sign Out */}
                    <div className="flex items-center gap-4">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="flex items-center gap-2 px-2 text-sm font-normal text-muted-foreground hover:text-foreground">
                                    <span className="hidden sm:inline">{user!.email || user!.phone || 'Account'}</span>
                                    <ChevronDown className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[220px]">
                                {partner && (
                                    <>
                                        <DropdownMenuLabel className="font-normal">
                                            <div className="flex flex-col space-y-1">
                                                <p className="text-sm font-semibold leading-none">{partner.name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    Partner since {format(new Date(partner.created_at), 'MMMM yyyy')}
                                                </p>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            {/* Page content */}
            <main className="p-4 lg:p-6">
                <Outlet />
            </main>
        </div>
    );
}

export default function PartnerLayout() {
    return (
        <RequireAuth>
            <PartnerChrome />
        </RequireAuth>
    );
}
